/**
 * ConsoleBand — a labelled region of a screen, in EITHER shell.
 *
 * WHY THIS EXISTS. Two rows of the 26 August change register are the same
 * complaint about two screens:
 *
 *   DSH-06 (M3 C2, re-asked in M4): "all of the boxes here, they are everywhere.
 *   It feels a bit all over the place. We need this a bit more structured...
 *   maybe immediate actions on top and then just a bit lower all the operational
 *   compliance and stuff."
 *
 *   BIL-01 (M2 00:38): "your billing is getting long. I need to understand
 *   what's going on here, please."
 *
 * Both screens were a single run of cards with nothing saying which card
 * belonged with which. The answer on both is the same: name the groups, so a
 * card sits inside a stated concern instead of floating in a wall of
 * equal-weight boxes. Putting that in one component rather than writing the
 * markup twice is the standing rule about a single source of truth per concern
 * — two hand-rolled versions is how two screens end up disagreeing about what a
 * section heading looks like.
 *
 * BOTH SHELLS, since the cohesion pass (2026-09-15). The console got this
 * pattern in August; the client portal never did, which is why the client
 * dashboard still reads as a wall of boxes. The contract
 * (docs/redesign-20260915/design_handoff_app_cohesion/README.md, "Bands") says
 * to extend this component rather than write a second one, so its CSS moved out
 * of the `.admin-v2` scope in design-system.css and its name stayed put: the
 * file is imported by the console screens that already use it, and renaming it
 * would be churn with no reader-visible payoff.
 *
 * Presentational only: no data, no queries, no role logic. Styling lives in
 * `styles/design-system.css` under `.console-band`.
 *
 * A BAND NEVER LABELS NOTHING (NEW-R12-DASH-EMPTY-GOVERNANCE-BAND, grader
 * browser pass 2). The client dashboard drew "GOVERNANCE" over an empty space,
 * straight into "THE BOOK": the screen decided to draw the band on a
 * permission, while its only card renders nothing when no resolution awaits a
 * signature, and nothing while it loads. A screen cannot see that from where
 * it renders the band, so the band checks its own body after every render and
 * whenever it changes, and hides itself, label and all, while the body shows
 * nothing. The cards stay mounted, so the band comes back the moment one of
 * them has something to say.
 */

/** Elements that are content even with no text: media, controls, anything
 *  with a role or an accessible name, and a declared loading region. */
const CONTENT_ELEMENTS =
  'img,svg,canvas,video,iframe,input,select,textarea,button,a[href],[role],[aria-label],[aria-busy="true"]';

/** Does anything in the band, below its head, show the reader something? */
function bandBodyHasContent(section: Element): boolean {
  for (const child of Array.from(section.children)) {
    if (child.classList.contains('console-band-head')) continue;
    if ((child.textContent ?? '').trim() !== '') return true;
    if (child.matches(CONTENT_ELEMENTS) || child.querySelector(CONTENT_ELEMENTS)) return true;
  }
  return false;
}

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface ConsoleBandProps {
  /** Slug used for the test id and the heading's `id`. Unique per screen. */
  id: string;
  /**
   * The band's name. Short, declarative, sentence case: `Needs you`,
   * `The estate`, `The book`, `Running work`. It says what the region IS, so a
   * user scanning at speed can skip it. Not a card title.
   */
  title: string;
  /** Optional right-hand line: a count, a caveat. Omit rather than pass ''. */
  note?: ReactNode;
  /** Extra classes on the `<section>`, for a screen that needs a layout hook. */
  className?: string;
  children: ReactNode;
}

/**
 * A band label never ends in a full stop.
 *
 * The contract's typography rule is one line: "headings never take a trailing
 * full stop", and the checks it asks for include "no heading text ends in `.`".
 * The product broke that rule about a dozen ways before this pass (`Allocation.`,
 * `Holdings & performance.`, `Your trusts.`, `Operational queue.`, `My queue.`),
 * so enforcing it where the heading is RENDERED makes it true by construction
 * instead of true until the next screen is written. An ellipsis is left alone:
 * that is a different character doing a different job.
 */
export function bandLabel(title: string): string {
  const trimmed = title.trim();
  return trimmed.endsWith('.') && !trimmed.endsWith('..')
    ? trimmed.slice(0, -1)
    : trimmed;
}

export function ConsoleBand({ id, title, note, className, children }: ConsoleBandProps) {
  const ref = useRef<HTMLElement>(null);
  const [empty, setEmpty] = useState(false);

  // Before paint, and again on every change below the band: a card that loads
  // late, or empties, moves the band with it. Same-value updates bail out, so
  // this never loops.
  useLayoutEffect(() => {
    const section = ref.current;
    if (!section) return undefined;
    const check = () => setEmpty(!bandBodyHasContent(section));
    check();
    if (typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(check);
    observer.observe(section, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className={className ? `console-band ${className}` : 'console-band'}
      data-testid={`console-band-${id}`}
      aria-labelledby={`console-band-${id}-h`}
      hidden={empty || undefined}
    >
      <div className="console-band-head">
        <h2 className="console-band-title" id={`console-band-${id}-h`}>
          {bandLabel(title)}
        </h2>
        <span className="console-band-rule" aria-hidden="true" />
        {note ? <span className="console-band-note">{note}</span> : null}
      </div>
      {children}
    </section>
  );
}
